import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { FaCheck, FaTimes, FaQuestionCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import Alert from '../common/Alert';
import IconButton from '../common/IconButton';
import CommunityIdentity from '../common/CommunityIdentity';
import Table from '../common/Table';
import useApi from '../../hooks/useApi';
import 'animate.css';

function ApproveUsers({ isSuperAdmin }) {
  const { session } = useContext(AuthContext);
  const { communityId } = session || {};
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [actionType, setActionType] = useState('');
  const [remark, setRemark] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const { get, put } = useApi();

  console.log('ApproveUsers - isSuperAdmin:', isSuperAdmin, 'communityId:', communityId);

  useEffect(() => {
    if (!isSuperAdmin && !communityId) {
      setError('Community ID is missing for admin.');
      return;
    }
    fetchUsers();
  }, [communityId, isSuperAdmin]);

  const fetchUsers = async () => {
    try {
      const params = !isSuperAdmin && communityId ? { communityId } : {};
      console.log('Fetching with params:', params);
      const response = await get('/api/admin/pending-users', { params });
      console.log('Fetched users:', response);
      if (!Array.isArray(response)) {
        console.error('Expected array, got:', response);
        setError('Invalid response format from server.');
        setUsers([]);
        return;
      }
      setUsers(response);
      response.forEach(user => {
        console.log('User community data:', user.community);
      });
      setError('');
    } catch (err) {
      console.error('Fetch error:', err.response?.data || err.message);
      setError('Failed to fetch pending users.');
      toast.error('Failed to fetch users.');
    }
  };

  const openRemarkModal = (id, action) => {
    setSelectedUserId(id);
    setActionType(action);
    setRemark('');
    setShowModal(true);
  };

  const closeRemarkModal = () => {
    setShowModal(false);
    setSelectedUserId(null);
    setActionType('');
    setRemark('');
  };

  const handleAction = async () => {
    const user = users.find((u) => u.id === selectedUserId);
    if (!user) {
      setError('User not found.');
      toast.error('User not found.');
      closeRemarkModal();
      return;
    }
    const trimmedRemark = remark.trim() || 'Action by admin';
    try {
      const payload = {
        name: user.name,
        mobileNo: user.mobileNo,
        remark: trimmedRemark,
        username: user.username,
        houseNo: user.houseNo,
        division: user.division,
        ownershipType: user.ownershipType,
        communityId: user.communityId,
      };
      await put(`/api/admin/${actionType}/${selectedUserId}`, payload);
      setUsers(users.filter((u) => u.id !== selectedUserId));
      toast.success(`User ${actionType}d successfully`);
      setError('');
      closeRemarkModal();
    } catch (err) {
      console.error(`${actionType} error:`, err.response?.data || err.message);
      setError(`Failed to ${actionType} user: ${err.message}`);
      toast.error(`Failed to ${actionType} user.`);
    }
  };

  const toggleExpand = (id) => {
    setExpandedUser(expandedUser === id ? null : id);
  };

  const columns = isSuperAdmin
    ? ['Username', 'Name', 'House No', 'Division', 'Ownership Type', 'Mobile No', 'Community', 'Actions']
    : ['Username', 'Name', 'House No', 'Division', 'Ownership Type', 'Mobile No', 'Actions'];

  console.log('Columns:', columns);

  const renderRow = (user) => (
    <tr key={user.id}>
      <td>{user.username}</td>
      <td>{user.name}</td>
      <td>{user.houseNo}</td>
      <td>{user.division}</td>
      <td>{user.ownershipType || 'N/A'}</td>
      <td>{user.mobileNo}</td>
      {isSuperAdmin && (
        <td>{user.community ? <CommunityIdentity community={user.community} size="xs" compact /> : 'N/A'}</td>
      )}
      <td>
        <IconButton
          icon={<FaCheck />}
          tooltip="Approve"
          variant="success"
          onClick={() => openRemarkModal(user.id, 'approve')}
          className="me-2"
        />
        <IconButton
          icon={<FaTimes />}
          tooltip="Reject"
          variant="danger"
          onClick={() => openRemarkModal(user.id, 'reject')}
        />
      </td>
    </tr>
  );

  return (
    <div className="container py-3 animate__animated animate__fadeIn">
      <h2 className="mb-4 fs-4 fw-bold">Approve Users</h2>
      {error && <Alert type="danger" message={error} />}
      {users.length === 0 && !error ? (
        <Alert type="info" message="No pending approvals" />
      ) : (
        <>
          <div className="card shadow mx-auto d-none d-md-block" style={{ maxWidth: '1200px' }}>
            <div className="card-body">
              <h5 className="card-title fs-5 mb-3">Pending Users</h5>
              <Table columns={columns} data={users} renderRow={renderRow} />
            </div>
          </div>
          <div className="d-md-none">
            {users.map((user) => {
              const isExpanded = expandedUser === user.id;
              return (
                <div
                  key={user.id}
                  className="card shadow-sm mb-3 animate__animated animate__fadeInUp"
                >
                  <div
                    className="card-header d-flex justify-content-between align-items-center bg-light"
                    onClick={() => toggleExpand(user.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <h6 className="mb-0">{user.username} - {user.name}</h6>
                    <span className="badge bg-warning text-dark">PENDING</span>
                  </div>
                  <div className="card-body">
                    <p className="mb-1">
                      <strong>House No:</strong> {user.houseNo || '-'}
                    </p>
                    {isExpanded && (
                      <>
                        <p className="mb-1">
                          <strong>Division:</strong> {user.division || '-'}
                        </p>
                        <p className="mb-1">
                          <strong>Ownership Type:</strong> {user.ownershipType || 'N/A'}
                        </p>
                        <p className="mb-1">
                          <strong>Mobile No:</strong> {user.mobileNo || '-'}
                        </p>
                        {isSuperAdmin && (
                          <div className="mb-1">
                            <strong>Community:</strong>{' '}
                            {user.community ? <CommunityIdentity community={user.community} size="xs" compact /> : 'N/A'}
                          </div>
                        )}
                        <div className="mt-2 d-flex gap-2">
                          <IconButton
                            icon={<FaCheck />}
                            tooltip="Approve"
                            variant="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              openRemarkModal(user.id, 'approve');
                            }}
                            size="sm"
                          />
                          <IconButton
                            icon={<FaTimes />}
                            tooltip="Reject"
                            variant="danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              openRemarkModal(user.id, 'reject');
                            }}
                            size="sm"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div
                    className="card-footer text-center bg-light"
                    onClick={() => toggleExpand(user.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    {isExpanded ? (
                      <FaChevronUp size={16} className="text-muted" />
                    ) : (
                      <FaChevronDown size={16} className="text-muted" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {actionType === 'approve' ? 'Approve User' : 'Reject User'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeRemarkModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="remarkInput" className="form-label">
                    Remark <FaQuestionCircle title="Optional remark for action" />
                  </label>
                  <textarea
                    className="form-control"
                    id="remarkInput"
                    rows="5"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    placeholder="Enter remark (default: Action by admin)"
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeRemarkModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAction}
                >
                  {actionType === 'approve' ? 'Approve' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

ApproveUsers.propTypes = {
  isSuperAdmin: PropTypes.bool,
};

export default ApproveUsers;
